<template>
  <div
    class="adaptive-debug"
    :class="{ collapsed }"
    data-test="adaptive-debug"
  >
    <button
      class="debug-toggle"
      @click="collapsed = !collapsed"
    >
      CAT debug {{ collapsed ? '▸' : '▾' }}
    </button>
    <div v-if="!collapsed">
      <p class="hint">
        Hover a label for what it means.
      </p>

      <h4>Test</h4>
      <dl>
        <template
          v-for="row in testRows"
          :key="row.label"
        >
          <dt v-tooltip="tip('Test', row.label, row.tip, row.now)">
            {{ row.label }}
          </dt>
          <dd
            v-tooltip="tip('Test', row.label, row.tip, row.now)"
            :class="row.class"
          >
            {{ row.value }}
          </dd>
        </template>
      </dl>

      <div
        v-tooltip="tip('Test', 'Ability bar', tips.intervalBar)"
        class="interval-bar"
      >
        <span
          class="interval"
          :style="intervalStyle"
        ></span>
        <span
          class="standard"
          :style="{ left: `${position(debug.passingStandard)}%` }"
        ></span>
        <span
          class="theta"
          :style="{ left: `${position(debug.theta)}%` }"
        ></span>
      </div>

      <template v-if="item">
        <h4>This question</h4>
        <dl>
          <template
            v-for="row in questionRows"
            :key="row.label"
          >
            <dt v-tooltip="tip('This question', row.label, row.tip, row.now)">
              {{ row.label }}
            </dt>
            <dd v-tooltip="tip('This question', row.label, row.tip, row.now)">
              {{ row.value }}
            </dd>
          </template>
        </dl>
        <p
          v-tooltip="tip('This question', 'Correct answer', tips.correctAnswer)"
          class="label"
        >
          Correct answer
        </p>
        <ul class="correct-answers">
          <li
            v-for="(answer, index) in item.correctAnswers"
            :key="index"
          >
            {{ answer }}
          </li>
          <li v-if="!item.correctAnswers.length">
            No correct answer found on this question
          </li>
        </ul>
        <button
          v-if="canFill"
          v-tooltip="tip('This question', 'Select correct answer', tips.fillCorrect)"
          class="fill-button"
          data-test="adaptive-debug-fill"
          @click="$emit('fill-correct', item.correctResponse)"
        >
          Select correct answer
        </button>
        <a
          v-if="item.cmsUrl"
          v-tooltip="tip('This question', 'CMS link', tips.adminLink)"
          class="admin-link"
          :href="item.cmsUrl"
          target="_blank"
          rel="noopener"
        >Open #{{ item.flashcardId }} in the CMS</a>
      </template>

      <template
        v-for="section in extraSections"
        :key="section.title"
      >
        <h4>{{ section.title }}</h4>
        <dl>
          <template
            v-for="row in section.rows"
            :key="row.label"
          >
            <dt v-tooltip="tip(section.title, row.label, row.tip, row.now)">
              {{ row.label }}
            </dt>
            <dd
              v-tooltip="tip(section.title, row.label, row.tip, row.now)"
              :class="row.class"
            >
              {{ row.value }}
            </dd>
          </template>
        </dl>
      </template>

      <template v-if="recentItems.length">
        <h4>Recent answers</h4>
        <table>
          <thead>
            <tr>
              <th v-tooltip="tip('Recent answers', '#', tips.position)">
                #
              </th>
              <th v-tooltip="tip('Recent answers', 'Id', tips.id)">
                Id
              </th>
              <th v-tooltip="tip('Recent answers', 'b', tips.difficulty)">
                b
              </th>
              <th v-tooltip="tip('Recent answers', 'Result', tips.result)">
                Result
              </th>
              <th v-tooltip="tip('Recent answers', 'θ after', tips.thetaAfter)">
                θ after
              </th>
              <th v-tooltip="tip('Recent answers', 'Need', tips.need)">
                Need
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in recentItems"
              :key="row.position"
            >
              <td>{{ row.position }}</td>
              <td>{{ row.flashcardId }}</td>
              <td>{{ row.b }}</td>
              <td :class="row.correct ? 'right' : 'wrong'">
                {{ row.correct ? '✓' : '✗' }}
              </td>
              <td>{{ row.theta }}</td>
              <td class="need">
                {{ row.clientNeed }}
              </td>
            </tr>
          </tbody>
        </table>
      </template>
    </div>
  </div>
</template>

<script>
// Theta axis shown in the interval bar
const AXIS_MIN = -4;
const AXIS_MAX = 4;

const escapeHtml = text => String(text).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\'': '&#39;' }[c]));

const round = value => Math.round(value * 1000) / 1000;

const signed = value => {
  const rounded = Math.round(value * 1000) / 1000;
  return rounded >= 0 ? `+${rounded}` : `${rounded}`;
};

const percent = value => (value === null || value === undefined ? 'n/a' : `${Math.round(value * 100)}%`);

export default {
  name: 'AdaptiveDebugPanel',
  props: {
    debug: {
      type: Object,
      required: true,
    },
    // Show the select-correct-answer button (only while a question is on screen)
    allowFill: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['fill-correct'],
  data() {
    return {
      collapsed: false,
      tips: {
        intervalBar: 'Blue: the 95% range for the student\'s ability. Yellow line: the passing standard. White line: the current ability estimate. The test can stop once question 85 is reached and the blue range sits entirely on one side of the yellow line.',
        correctAnswer: 'What the server scores as right. All parts must match; there is no partial credit.',
        fillCorrect: 'Fills in the correct answer on the question so you can press Next and test a right answer. Change it before pressing Next to test a wrong one.',
        adminLink: 'Open this question for editing in the CMS (cms.hltcorp.com) in a new tab.',
        position: 'Question number in this attempt.',
        id: 'Question id.',
        difficulty: 'Question difficulty (b). Higher is harder.',
        result: 'Whether the server scored the answer right (✓) or wrong (✗).',
        thetaAfter: 'Ability estimate (θ) after this answer.',
        need: 'Client needs area the question was picked for.',
      },
    };
  },
  computed: {
    item() {
      return this.debug.currentItem;
    },
    canFill() {
      const response = this.item?.correctResponse;
      return this.allowFill && !!(response?.rawAnswerData || response?.answerIds?.length);
    },
    testRows() {
      const d = this.debug;
      const [low, high] = d.interval;
      const std = d.passingStandard;
      const gap = round(d.theta - std);
      const remainingToMin = Math.max(d.minItems - d.itemsAnswered, 0);
      const pctRight = d.itemsAnswered ? Math.round((d.numCorrect / d.itemsAnswered) * 100) : 0;

      return [
        {
          label: 'Ability (θ)',
          value: `${d.theta} ± ${d.se}`,
          tip: 'The student\'s estimated ability on the logit scale, plus or minus its standard error. It starts at 0, goes up after a right answer and down after a wrong one. The ± shrinks as more questions are answered.',
          now: `Best guess ${d.theta}, which is ${gap >= 0 ? `${gap} above` : `${-gap} below`} the standard of ${std}. The ± of ${d.se} is how unsure that guess still is.`,
        },
        {
          label: '95% range',
          value: `${low} to ${high}`,
          tip: 'θ ± 1.96 × standard error. The test is 95% sure the student\'s ability is inside this range.',
          now: `The student's ability is very likely between ${low} and ${high}. ${this.rangeVsStandard}`,
        },
        {
          label: 'Standard',
          value: std,
          tip: 'The passing standard on the same scale as θ. Set in the sim config adaptive_settings (passing_standard).',
          now: `${std} on the θ scale. ${std === 0 ? '0 is about where an average QBank user sits, since difficulty comes from our own answer data.' : ''}`,
        },
        {
          label: 'Decision',
          value: {
            above: 'Above the standard: passes if it stops now',
            below: 'Below the standard: fails if it stops now',
            undecided: 'Not decided yet',
          }[d.decision],
          class: `decision-${d.decision}`,
          tip: 'Whether the whole 95% range is above the standard, below it, or still crosses it. After the minimum number of questions, the test stops as soon as this is above or below.',
          now: d.decision === 'undecided'
            ? `The range ${low} to ${high} still includes the standard ${std}, so more questions are needed.`
            : `The range ${low} to ${high} is entirely ${d.decision} the standard ${std}. ${remainingToMin ? `It can't stop until ${remainingToMin} more questions are answered.` : 'The test stops after this answer is recorded.'}`,
        },
        this.projectionRow,
        {
          label: 'Answered',
          value: `${d.itemsAnswered} (${d.numCorrect} right)`,
          tip: 'Questions answered so far in this attempt and how many were right. Percent right is not the score; the ability estimate is.',
          now: `${pctRight}% right so far. Because each question is matched to the student, most students end up near 50% right whether they pass or fail.`,
        },
        {
          label: 'Length',
          value: `${d.minItems} to ${d.maxItems}`,
          tip: 'The test never stops before the minimum. At the maximum it stops and passes if θ is above the standard.',
          now: remainingToMin
            ? `${remainingToMin} more questions before it is allowed to stop; at most ${d.maxItems - d.itemsAnswered} more.`
            : `Past the minimum; it stops as soon as the decision is made, or after ${d.maxItems - d.itemsAnswered} more questions at most.`,
        },
        {
          label: 'Pool',
          value: `${d.poolSize} questions${d.balanced ? '' : ' (client needs not mapped)'}`,
          tip: 'How many questions the CAT can choose from: published, visible, standalone questions of a type the sim supports, with enough answer data. "Client needs not mapped" means questions are picked by difficulty only, with no spread across the 8 NCLEX areas.',
          now: `${d.poolSize} questions available. Questions from this attempt are never repeated, and ones the student has never answered in the app are preferred.`,
        },
      ];
    },
    projectionRow() {
      const p = this.debug.projection;
      const value = {
        decided: `${p.questionsLeft} (decided, waiting for the minimum)`,
        too_close: `up to ${p.questionsLeft} (too close to call)`,
        max: `${p.questionsLeft} (likely runs to the maximum)`,
        estimate: `about ${p.questionsLeft}`,
      }[p.reason];
      const now = {
        decided: p.questionsLeft
          ? `The result is already clear; the test keeps going only because it hasn't reached the minimum. ${p.questionsLeft} more to go.`
          : 'The result is clear and the minimum is reached, so the test stops after this answer is recorded.',
        too_close: `θ is almost exactly on the standard, so the range can't clear it soon. If θ stays here, the test runs the remaining ${p.questionsLeft} questions to the maximum.`,
        max: `To clear the standard from here the ± has to shrink to ${p.targetSe}, which takes more questions than are left. Expect it to end at the maximum unless θ moves away from the standard.`,
        estimate: `To clear the standard from here the ± has to shrink to ${p.targetSe}. That takes about ${p.questionsLeft} more well-matched questions if θ stays where it is.`,
      }[p.reason];
      return {
        label: 'Questions left',
        value,
        tip: 'A rough estimate of how many more questions until the test stops. It assumes θ stays where it is and each question is well matched, so it changes as answers come in. The test can only stop between the minimum and maximum length.',
        now,
      };
    },
    extraSections() {
      const sections = [];
      const sel = this.debug.selection;
      const out = this.debug.outcomes;

      if (out) {
        sections.push({
          title: 'If answered',
          rows: [
            {
              label: 'Right',
              value: `θ ${out.right.theta} ± ${out.right.se}`,
              class: 'right',
              tip: 'The ability estimate the student will have if they get this question right.',
              now: `θ would move from ${this.debug.theta} to ${out.right.theta} (${signed(out.right.theta - this.debug.theta)}).`,
            },
            {
              label: 'Wrong',
              value: `θ ${out.wrong.theta} ± ${out.wrong.se}`,
              class: 'wrong',
              tip: 'The ability estimate the student will have if they get this question wrong.',
              now: `θ would move from ${this.debug.theta} to ${out.wrong.theta} (${signed(out.wrong.theta - this.debug.theta)}).`,
            },
          ],
        });
      }

      if (sel) {
        sections.push({
          title: 'Why this question',
          rows: [
            {
              label: 'Matched to θ',
              value: sel.thetaAtServe,
              tip: 'The ability estimate when this question was picked. The engine looks for questions with difficulty (b) close to this number.',
              now: `Picked when θ was ${sel.thetaAtServe}; this question's b is ${this.item?.b}.`,
            },
            {
              label: 'Client need',
              value: sel.needTargetPct === null
                ? `${sel.need} (no balancing)`
                : `${sel.need}: ${sel.needServedBefore} served, target ${sel.needTargetPct}%`,
              tip: 'The client needs area the engine chose before picking a question. It picks the area furthest behind its target share of the test.',
              now: sel.needTargetPct === null
                ? 'Client needs are not mapped, so every question comes from one shared pool.'
                : `${sel.needServedBefore} questions from this area were served before this one; its target is ${sel.needTargetPct}% of the test.`,
            },
            {
              label: 'Candidates',
              value: sel.candidates
                ? `${sel.candidates}, b ${sel.candidateBRange[0]} to ${sel.candidateBRange[1]}`
                : 'none',
              tip: 'The questions this one was randomly picked from, so the same questions are not given to every student. They are the questions closest in difficulty to θ (top_k in the settings), limited to unseen ones when possible.',
              now: sel.candidates
                ? `This question was one of ${sel.candidates} candidates with b between ${sel.candidateBRange[0]} and ${sel.candidateBRange[1]}.`
                : 'No candidates could be rebuilt for this question.',
            },
            {
              label: 'Picked from',
              value: sel.pickedFrom === 'unseen' ? 'unseen questions' : 'closest questions',
              tip: 'Questions the student has never answered anywhere in the app are preferred. Questions they have answered elsewhere are only used when no unseen question is close enough in difficulty (within seen_tolerance in the settings). Questions from this attempt are never repeated.',
              now: sel.pickedFrom === 'unseen'
                ? 'Drawn only from questions this student has never answered in the app.'
                : `No unseen question was close enough in difficulty, so it was drawn from the ${sel.nearestSize} closest questions, ${sel.nearestSeen} of which the student had answered elsewhere in the app.`,
            },
            {
              label: 'Seen before',
              value: sel.seenInApp ? 'yes, elsewhere in the app' : 'no',
              class: sel.seenInApp ? 'wrong' : null,
              tip: 'Whether the student has answered this question before, in practice, quizzes, other sims or an earlier CAT attempt. It is never one they already had in this attempt.',
              now: sel.seenInApp
                ? 'The student has answered this question before outside this attempt.'
                : 'The student has never answered this question before.',
            },
          ],
        });
      }

      return sections;
    },
    rangeVsStandard() {
      const [low, high] = this.debug.interval;
      const std = this.debug.passingStandard;
      if (low > std) return `All of it is above the standard (${std}), so the student would pass.`;
      if (high < std) return `All of it is below the standard (${std}), so the student would fail.`;
      return `The standard (${std}) falls inside it, so the result isn't decided yet.`;
    },
    questionRows() {
      const item = this.item;
      const theta = this.debug.theta;
      const diff = round(item.b - theta);
      const difficultyWord = item.b > 0.5 ? 'harder than average' : (item.b < -0.5 ? 'easier than average' : 'about average');

      return [
        {
          label: 'Id',
          value: item.flashcardId,
          tip: 'Question (flashcard) id.',
        },
        {
          label: 'Type',
          value: item.type,
          tip: 'Question type as the web app receives it.',
        },
        {
          label: 'Difficulty (b)',
          value: item.b,
          tip: 'Difficulty on the same scale as θ, worked out from how many students got it right. 0 is average, negative is easier, positive is harder. The engine picks questions with b close to the current θ.',
          now: `${item.b} is ${difficultyWord}. It is ${diff >= 0 ? `${diff} above` : `${-diff} below`} the student's θ of ${theta}.`,
        },
        {
          label: 'Students right',
          value: `${percent(item.shareCorrect)} of ${item.numAnswered ?? 0}`,
          tip: 'Share of all answers to this question across the app that were right, and how many answers that is based on. This is where b comes from.',
          now: `${percent(item.shareCorrect)} of ${item.numAnswered ?? 0} answers were right. ${item.numAnswered < 200 ? 'That is a small sample, so b is rough.' : 'That is enough answers for b to be reliable.'}`,
        },
        {
          label: 'p',
          value: item.p ?? 'n/a',
          tip: 'The question\'s p value from app_flashcard_meta_data: the share of students who answered it right, as last calculated by the stats job. The CAT does not use it; b comes from the live answer counts instead, because the stats job is no longer scheduled.',
          now: item.p === null || item.p === undefined
            ? 'No p value stored for this question.'
            : `${percent(item.p)} right when last calculated, against ${percent(item.shareCorrect)} from the live counts. A big gap means the stored value is out of date.`,
        },
        {
          label: 'pb',
          value: item.pb ?? 'n/a',
          tip: 'Point-biserial correlation from app_flashcard_meta_data: how well the question separates strong students from weak ones. Above 0.2 is good, 0.1 to 0.2 is weak, and near 0 or negative means strong students do no better (or worse) than weak ones, which usually points to a flawed question. The CAT does not use it.',
          now: item.pb === null || item.pb === undefined
            ? 'No pb value stored for this question.'
            : `${item.pb} is ${item.pb >= 0.2 ? 'good' : (item.pb >= 0.1 ? 'weak' : 'poor; worth reviewing the question')}.`,
        },
        {
          label: 'Chance right',
          value: percent(item.expectedCorrect),
          tip: 'The model\'s chance that a student at the current θ gets this question right. Near 50% means the question is well matched to the student.',
          now: `${percent(item.expectedCorrect)} chance for a student at θ ${theta}. ${Math.abs(item.expectedCorrect - 0.5) <= 0.15 ? 'Well matched: the answer tells the test a lot.' : 'Not closely matched: the answer tells the test less.'}`,
        },
        {
          label: 'Client need',
          value: item.clientNeed,
          tip: 'The NCLEX client needs area this question was picked for. Shows "all" until client needs are mapped to categories.',
        },
        {
          label: 'Diagnostic',
          value: item.diagnosticCategory || 'none',
          tip: 'The question\'s diagnostic category. Used to place it in a client needs area once those are mapped.',
        },
        {
          label: 'Categories',
          value: item.categories.join(', ') || 'none',
          tip: 'Categories the question is visibly attached to in this app.',
        },
      ];
    },
    intervalStyle() {
      const left = this.position(this.debug.interval[0]);
      return { left: `${left}%`, width: `${this.position(this.debug.interval[1]) - left}%` };
    },
    recentItems() {
      return [...this.debug.items].reverse().slice(0, 15);
    },
  },
  methods: {
    // floating-vue tooltip naming the section and item, then explaining it
    tip(section, label, text, now) {
      const current = now ? `<div class="cat-debug-tip-now">${escapeHtml(now)}</div>` : '';
      const content = `<div class="cat-debug-tip-title">${escapeHtml(section)} › ${escapeHtml(label)}</div>${escapeHtml(text)}${current}`;
      return { content, html: true, placement: 'left', delay: { show: 100, hide: 0 }, popperClass: 'cat-debug-tip' };
    },
    position(theta) {
      const clamped = Math.min(Math.max(theta, AXIS_MIN), AXIS_MAX);
      return ((clamped - AXIS_MIN) / (AXIS_MAX - AXIS_MIN)) * 100;
    },
  },
};
</script>

<style scoped lang="scss">
.adaptive-debug {
  position: fixed;
  right: 1rem;
  bottom: 5rem;
  z-index: 1000;
  width: 26rem;
  max-height: 75vh;
  overflow-y: auto;
  padding: .75rem;
  background: rgba(20, 24, 31, .94);
  color: #e6e9ee;
  font-size: .8rem;
  border-radius: .5rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, .3);

  &.collapsed {
    width: auto;
  }
}

.debug-toggle {
  background: none;
  border: none;
  color: inherit;
  font-weight: 600;
  padding: 0;
  cursor: pointer;
}

.hint {
  color: #9aa5b4;
  margin: .25rem 0 0;
}

h4 {
  font-size: .8rem;
  margin: .75rem 0 .25rem;
  color: #ffd166;
}

dl {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: .25rem .75rem;
  margin: 0;
}

dt {
  color: #9aa5b4;
  cursor: help;
  text-decoration: underline dotted;
}

dd {
  margin: 0;
  cursor: help;
  overflow-wrap: anywhere;
}

.label {
  color: #9aa5b4;
  margin: .5rem 0 .25rem;
  cursor: help;
  text-decoration: underline dotted;
}

.correct-answers {
  margin: 0;
  padding-left: 1.1rem;
  color: #5fd38d;
}

.fill-button {
  display: block;
  margin-top: .5rem;
  padding: .3rem .6rem;
  background: #2f8f5b;
  border: none;
  border-radius: .25rem;
  color: white;
  font-size: .8rem;
  cursor: pointer;
}

.admin-link {
  display: inline-block;
  margin-top: .5rem;
  color: #7fb3ff;
}

dd.right {
  color: #5fd38d;
}

dd.wrong {
  color: #ff7a66;
}

.decision-above {
  color: #5fd38d;
}

.decision-below {
  color: #ff7a66;
}

.interval-bar {
  position: relative;
  height: 1rem;
  margin: .75rem 0 0;
  background: #333b47;
  border-radius: .25rem;
  cursor: help;

  span {
    position: absolute;
    top: 0;
    bottom: 0;
  }

  .interval {
    background: rgba(90, 160, 255, .5);
  }

  .standard {
    width: 2px;
    background: #ffd166;
  }

  .theta {
    width: 2px;
    background: white;
  }
}

table {
  width: 100%;
  border-collapse: collapse;

  th, td {
    text-align: left;
    padding: .1rem .25rem;
  }

  th {
    color: #9aa5b4;
    font-weight: normal;
    cursor: help;
    text-decoration: underline dotted;
  }

  .right {
    color: #5fd38d;
  }

  .wrong {
    color: #ff7a66;
  }

  .need {
    max-width: 7rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>

<style lang="scss">
// Tooltips render outside the panel, so this can't be scoped
.cat-debug-tip .v-popper__inner {
  max-width: 20rem;
  white-space: normal;
  overflow-wrap: break-word;
  font-size: .8rem;
  line-height: 1.4;
}

.cat-debug-tip-now {
  margin-top: .4rem;
  padding-top: .4rem;
  border-top: 1px solid rgba(255, 255, 255, .2);
  color: #cfe3ff;
}

.cat-debug-tip-title {
  font-weight: 600;
  color: #ffd166;
  margin-bottom: .25rem;
}
</style>
